import React from 'react';
import { curveNatural } from '@visx/curve';
import { ComposedChart } from '../charts/composed-chart';
import { BarChart } from '../charts/bar-chart';
import { Area } from '../charts/area';
import { Grid } from '../charts/grid';
import './ReportsNative.css';

/**
 * Structural skeletons for report first-load.
 *
 * These mirror the REAL layout element for element — same joined-sheet
 * wrapper, same card head, same tile anatomy, same heights — so the
 * skeleton is a preview of the page rather than a placeholder blob, and
 * nothing shifts when data lands.
 *
 * Styling follows the bklit kit's loading language (see
 * components/charts/LODGEIC.md): a recessive surface with a slow shimmer
 * sweep, never solid grey boxes. The plot area is not imitated in CSS — it
 * is the kit's own loading chart (`SkeletonChartPlot`), so a placeholder
 * panel animates exactly like the chart that replaces it.
 *
 * Where the series config is already known, prefer passing `loading` to the
 * chart wrapper itself: it renders the same loading state and then *morphs*
 * into the data. Reach for these panels when there is no payload yet — page
 * first load — and pass `kind`/`kinds` so each placeholder matches the
 * family (area/line vs column) of the card it stands in for.
 */

const SKELETON_NO_DATA = [];
// The plot box already supplies the gutters, so the kit chart fills it.
const SKELETON_PLOT_MARGIN = { top: 4, right: 2, bottom: 4, left: 0 };

/**
 * The plot itself, drawn by the bklit kit in its own loading state rather
 * than faked in CSS: a shimmering grid under a sweeping skeleton series for
 * area/line panels, swept placeholder bars for column panels. A loading
 * panel therefore animates exactly like the chart that replaces it, and the
 * two states share one implementation.
 *
 * `kind` picks the family — pass the one the real card renders.
 */
export function SkeletonChartPlot({ kind = 'area' }) {
  if (kind === 'bar') {
    return (
      <BarChart
        aspectRatio={null}
        className="rn-bklit rn-sk-kitchart"
        data={SKELETON_NO_DATA}
        margin={SKELETON_PLOT_MARGIN}
        status="loading"
        style={{ height: '100%' }}
      />
    );
  }
  return (
    <ComposedChart
      animationDuration={0}
      aspectRatio={null}
      className="rn-bklit rn-sk-kitchart"
      data={SKELETON_NO_DATA}
      margin={SKELETON_PLOT_MARGIN}
      // Empty data makes the kit shell synthesize its own skeleton series,
      // keyed { date, value } — so the x/y keys below are the kit's, not ours.
      status="loading"
      style={{ height: '100%' }}
      xDataKey="date"
    >
      <Grid horizontal numTicksRows={4} />
      <Area
        curve={curveNatural}
        dataKey="value"
        fadeEdges={false}
        fill="transparent"
        fillOpacity={0}
        loading
        // Diagonal shimmer travelling across the skeleton line, per the kit's
        // "sweep" loading style.
        loadingStyle="sweep"
        showHighlight={false}
        showLine
        stroke="transparent"
        strokeWidth={2}
      />
    </ComposedChart>
  );
}

/** One shimmering bar. `w`/`h` accept any CSS length. */
export function SkeletonLine({ w = '100%', h = 10, radius, style, className }) {
  return (
    <span
      className={className ? `rn-sk ${className}` : 'rn-sk'}
      style={{ width: w, height: h, ...(radius !== undefined ? { borderRadius: radius } : null), ...style }}
    />
  );
}

/** Card head placeholder: title left, sub right — matches .rn-card-head. */
export function SkeletonCardHead({ titleWidth = 120, subWidth = 96 }) {
  return (
    <div className="rn-card-head">
      <SkeletonLine w={titleWidth} h={12} />
      <SkeletonLine w={subWidth} h={10} />
    </div>
  );
}

/** KPI tile matching .rn-ws-kpi: label + delta, big value, spark, note. */
export function KpiTileSkeleton({ spark = true, note = true }) {
  return (
    <div className="rn-card rn-ws-kpi">
      <div className="rn-ws-kpi-top">
        <SkeletonLine w={62} h={9} />
        <SkeletonLine w={38} h={14} radius={999} />
      </div>
      <SkeletonLine w="65%" h={26} />
      {spark && (
        <div className="rn-ws-kpi-spark">
          <SkeletonLine w="100%" h={40} />
        </div>
      )}
      {note && <SkeletonLine w="80%" h={10} />}
    </div>
  );
}

/** A row of KPI tiles, joined (no gaps) exactly like the live row. */
export function KpiRowSkeleton({ count = 5, className = 'rn-ws-kpis', ...tile }) {
  return (
    <div className={`${className} rn-joinrow`}>
      {Array.from({ length: count }, (_, i) => (
        <KpiTileSkeleton key={i} {...tile} />
      ))}
    </div>
  );
}

/**
 * Just the axis-and-plot frame, for a chart loading INSIDE a card that
 * already renders its own head (in-card loading, modals).
 */
export function ChartPlotSkeleton({ height = 196, axes = true, kind = 'area' }) {
  return (
    <>
      <div className="rn-sk-chart" style={{ height }}>
        {axes && (
          <div className="rn-sk-chart-y">
            {Array.from({ length: 4 }, (_, i) => (
              <SkeletonLine h={9} key={i} w={i === 0 ? 30 : 26} />
            ))}
          </div>
        )}
        <div className="rn-sk-chart-plot">
          <SkeletonChartPlot kind={kind} />
        </div>
      </div>
      {axes && (
        <div className="rn-sk-chart-x">
          {Array.from({ length: 5 }, (_, i) => (
            <SkeletonLine h={9} key={i} w={40} />
          ))}
        </div>
      )}
    </>
  );
}

/**
 * Chart panel placeholder: real card head plus an axis-and-plot frame, so
 * the gutters line up with the chart that replaces it.
 *
 * `stat` adds the KPI line (big value + delta chip) that trend-KPI cards
 * carry between the head and the plot; `axes` off drops the tick gutters
 * for sparkline-style cards that don't render them.
 */
export function ChartPanelSkeleton({ height = 196, stat = false, axes = true, kind = 'area' }) {
  if (!axes) {
    return (
      <div className="rn-card">
        <SkeletonCardHead />
        {stat && (
          <div className="rn-sk-stat">
            <SkeletonLine h={26} w="52%" />
            <SkeletonLine h={16} radius={999} w={70} />
          </div>
        )}
        <div className="rn-sk-chart" style={{ height }}>
          <div className="rn-sk-chart-plot">
            <SkeletonChartPlot kind={kind} />
          </div>
        </div>
      </div>
    );
  }
  return (
    <div className="rn-card">
      <SkeletonCardHead />
      {stat && (
        <div className="rn-sk-stat">
          <SkeletonLine h={26} w="52%" />
          <SkeletonLine h={16} radius={999} w={70} />
        </div>
      )}
      <div className="rn-sk-chart" style={{ height }}>
        <div className="rn-sk-chart-y">
          {Array.from({ length: 4 }, (_, i) => (
            <SkeletonLine h={9} key={i} w={i === 0 ? 30 : 26} />
          ))}
        </div>
        <div className="rn-sk-chart-plot">
          <SkeletonChartPlot kind={kind} />
        </div>
      </div>
      <div className="rn-sk-chart-x">
        {Array.from({ length: 5 }, (_, i) => (
          <SkeletonLine h={9} key={i} w={40} />
        ))}
      </div>
    </div>
  );
}

/**
 * A joined row of chart panels. `kinds` mirrors the real row's chart
 * families position by position (e.g. ['area', 'area', 'bar', 'area']);
 * anything it doesn't cover falls back to an area panel.
 */
export function ChartRowSkeleton({ count = 3, height = 196, minColumn = 320, stat = false, axes = true, kinds }) {
  return (
    <div
      className="rn-chartgrid"
      style={{ display: 'grid', gridTemplateColumns: `repeat(auto-fit, minmax(${minColumn}px, 1fr))` }}
    >
      {Array.from({ length: count }, (_, i) => (
        <ChartPanelSkeleton axes={axes} height={height} key={i} kind={kinds?.[i] || 'area'} stat={stat} />
      ))}
    </div>
  );
}

/**
 * In-table loading rows for a table that already renders its own head
 * (ReportDetailTable, ReportMatrix). New look only; Classic keeps its block.
 */
export function SkeletonTableRows({ columns = 6, rows = 6 }) {
  const cols = Array.from({ length: Math.max(1, columns) }, (_, i) => i);
  return Array.from({ length: rows }, (_, r) => (
    <tr className="rn-sk-trow rn-sk-inset-only" key={`sk-${r}`} aria-hidden="true">
      {cols.map((c) => (
        <td key={c} className={c === 0 ? undefined : 'rn-align-right'}>
          <SkeletonLine
            h={10}
            w={c === 0 ? 110 + ((r * 29) % 70) : 46 + (((r + 1) * (c + 3)) % 28)}
            style={c === 0 ? undefined : { marginLeft: 'auto' }}
          />
        </td>
      ))}
    </tr>
  ));
}

/** Table placeholder: toolbar, column header band, then striped rows. */
export function TableSkeleton({ rows = 6, columns = 7 }) {
  return (
    <div className="rn-card">
      <div className="rn-sk-table-head">
        <SkeletonLine w={168} h={16} />
        <SkeletonLine w={132} h={20} radius={999} />
        <span className="rn-sk-table-tools rn-sk-inset-only">
          <SkeletonLine w={120} h={26} radius={8} />
          <SkeletonLine w={28} h={28} radius={8} />
          <SkeletonLine w={28} h={28} radius={8} />
        </span>
      </div>
      <div className="rn-sk-row rn-sk-row-head" style={{ '--sk-cols': columns }}>
        {Array.from({ length: columns }, (_, i) => (
          <SkeletonLine h={9} key={i} w={i === 0 ? '58%' : '70%'} />
        ))}
      </div>
      {Array.from({ length: rows }, (_, r) => (
        <div className="rn-sk-row" key={r} style={{ '--sk-cols': columns }}>
          {Array.from({ length: columns }, (_, i) => (
            <SkeletonLine h={10} key={i} w={i === 0 ? '82%' : '55%'} />
          ))}
        </div>
      ))}
      <div className="rn-sk-row rn-sk-row-foot rn-sk-inset-only" style={{ '--sk-cols': columns }}>
        {Array.from({ length: columns }, (_, i) => (
          <SkeletonLine className="rn-sk-ink" h={10} key={i} w={i === 0 ? '36%' : '58%'} />
        ))}
      </div>
    </div>
  );
}

/**
 * Whole-report first load: KPI row → chart row → table, inside the same
 * joined sheet the loaded page uses.
 */
export function ReportSkeleton({
  kpis = 5,
  charts = 3,
  chartHeight = 196,
  chartKinds,
  tableRows = 6,
  tableColumns = 7,
  spark = true,
}) {
  return (
    <div className="rn-sheet" aria-busy="true" aria-label="Loading report">
      {kpis > 0 && <KpiRowSkeleton count={kpis} spark={spark} />}
      {charts > 0 && <ChartRowSkeleton count={charts} height={chartHeight} kinds={chartKinds} />}
      <TableSkeleton columns={tableColumns} rows={tableRows} />
    </div>
  );
}

export default ReportSkeleton;
